import React, { useEffect, useRef, useState } from "react";
import { Search, Check, Loader2 } from "lucide-react";

const inp = "w-full mt-1 bg-pgt-bg border border-white/15 rounded-sm px-3 py-2 text-white outline-none focus:border-pgt-green";
const lbl = "text-xs uppercase tracking-wider text-white/50";

// Free UK postcode lookup via postcodes.io (no API key). Type a postcode, pick it from the
// dropdown, and the town/county/country auto-fill; the customer only adds house number + street.
export const AddressFinder = ({ form, set }) => {
  const [q, setQ] = useState("");
  const [sug, setSug] = useState([]);
  const [open, setOpen] = useState(false);
  const [looking, setLooking] = useState(false);
  const boxRef = useRef(null);

  useEffect(() => {
    const term = q.trim();
    if (term.length < 2) { setSug([]); return; }
    const t = setTimeout(async () => {
      try {
        const r = await fetch(`https://api.postcodes.io/postcodes/${encodeURIComponent(term)}/autocomplete`);
        const j = await r.json();
        setSug(Array.isArray(j.result) ? j.result : []);
        setOpen(true);
      } catch { setSug([]); }
    }, 250);
    return () => clearTimeout(t);
  }, [q]);

  useEffect(() => {
    const onDoc = (e) => { if (boxRef.current && !boxRef.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, []);

  const choose = async (pc) => {
    setQ(pc); setOpen(false); setLooking(true);
    try {
      const r = await fetch(`https://api.postcodes.io/postcodes/${encodeURIComponent(pc)}`);
      const j = await r.json();
      const res = j.result || {};
      set("postcode", res.postcode || pc);
      set("city", res.admin_district || res.parish || res.admin_ward || "");
      set("county", res.admin_county || res.region || "");
      set("country", "United Kingdom");
    } catch { /* keep whatever the customer typed */ }
    setLooking(false);
  };

  // A postcode typed into the finder but not picked from the dropdown must still count as the postcode.
  const adoptTyped = () => { const term = q.trim(); if (term && !(form.postcode || "").trim()) set("postcode", term.toUpperCase()); };

  const chosen = !!(form.postcode || "").trim();

  return (
    <div className="space-y-3">
      <div className="relative" ref={boxRef}>
        <label className={lbl}>Find your address</label>
        <div className="relative">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-white/40" />
          <input value={q} onChange={(e) => setQ(e.target.value)} onFocus={() => sug.length && setOpen(true)} onBlur={() => setTimeout(adoptTyped, 200)}
            onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); adoptTyped(); setOpen(false); } }}
            placeholder="Start typing your postcode…" data-testid="delivery-postcode-search"
            className={inp + " pl-9"} />
          {looking && <Loader2 size={16} className="absolute right-3 top-1/2 -translate-y-1/2 text-pgt-green animate-spin" />}
        </div>
        {open && sug.length > 0 && (
          <ul data-testid="pc-suggestions"
            className="absolute z-20 mt-1 w-full max-h-56 overflow-auto bg-[#0d0d0d] border border-white/15 rounded-sm shadow-xl">
            {sug.map((pc) => (
              <li key={pc}>
                <button type="button" onClick={() => choose(pc)} data-testid={`pc-suggestion-${pc.replace(/\s+/g, "-")}`}
                  className="w-full text-left px-3 py-2 text-sm text-white/90 hover:bg-pgt-green/15 hover:text-white transition-colors">
                  {pc}
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>

      <div>
        <label className={lbl}>House number & street *</label>
        <input value={form.address1 || ""} onChange={(e) => set("address1", e.target.value)} data-testid="delivery-address1"
          placeholder="e.g. 12 High Street" className={inp} />
      </div>
      <div>
        <label className={lbl}>Address line 2 (optional)</label>
        <input value={form.address2 || ""} onChange={(e) => set("address2", e.target.value)} data-testid="delivery-address2"
          placeholder="Flat, unit, building…" className={inp} />
      </div>

      {/* Required address fields are always visible so the customer can always complete them. */}
      <div className="grid grid-cols-2 gap-3">
        <div>
          <label className={lbl}>Town / City *</label>
          <input value={form.city || ""} onChange={(e) => set("city", e.target.value)} data-testid="delivery-city" className={inp} />
        </div>
        <div>
          <label className={lbl}>County</label>
          <input value={form.county || ""} onChange={(e) => set("county", e.target.value)} data-testid="delivery-county" className={inp} />
        </div>
        <div className="col-span-2">
          <label className={lbl}>Postcode *</label>
          <input value={form.postcode || ""} onChange={(e) => set("postcode", e.target.value)} data-testid="delivery-postcode" className={inp} />
        </div>
      </div>

      {chosen && (
        <div className="flex items-center gap-2 text-xs text-white/60" data-testid="address-confirmed">
          <Check size={14} className="text-pgt-green" />
          <span>{[form.city, form.postcode].filter(Boolean).join(", ")} · United Kingdom</span>
        </div>
      )}
    </div>
  );
};

export default AddressFinder;
